import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Bell, MapPin, Pencil, Trash2, ChevronLeft } from 'lucide-react';
import { getCrimeTypes, getCrimeDisplayName } from '../../constants/crimes';
import './AlertsModal.css';

const FREQUENCY_OPTIONS = [
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly',  label: 'Yearly' }
];

const CHANNEL_OPTIONS = [
  { value: 'in_app', label: 'In-App' },
  { value: 'email',  label: 'Email' }
];

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getAlertCrimeTypes = (alert) => {
  if (Array.isArray(alert.crimeTypes) && alert.crimeTypes.length > 0) return alert.crimeTypes;
  if (alert.crimeType) return [alert.crimeType];
  return ['all_crime'];
};

const getCrimeLabel = (value) => {
  if (value === 'all_crime') return 'All Crime';
  return getCrimeDisplayName(value, 'FBI_CIUS');
};

const DEFAULT_FORM = {
  locationName: '',
  locationType: '',
  crimeTypes: ['all_crime'],
  frequency: 'weekly',
  channels: ['in_app']
};

const AlertsModal = ({
  isOpen,
  onClose,
  alerts = [],
  onSave,
  onUpdate,
  onDelete,
  pendingAlertData,
  defaultTab = 'notifications',
  notifications = [],
  onMarkRead,
  onMarkAllRead
}) => {
  const [view, setView] = useState('notifications');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [selectedNotifIds, setSelectedNotifIds] = useState([]);
  const [isCrimeDropdownOpen, setIsCrimeDropdownOpen] = useState(false);
  const crimeButtonRef = useRef(null);
  const crimeMenuRef = useRef(null);

  const crimeTypesList = getCrimeTypes('FBI_CIUS');

  const sortedNotifications = [...notifications].sort(
    (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt)
  );
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (!isOpen) {
      setView('notifications');
      setEditingId(null);
      setSaving(false);
      setSelectedNotifIds([]);
      setIsCrimeDropdownOpen(false);
      return;
    }
    if (pendingAlertData) {
      setFormData({
        locationName: pendingAlertData.locationName || '',
        locationType: pendingAlertData.locationType || '',
        crimeTypes: pendingAlertData.crimeType
          ? [pendingAlertData.crimeType]
          : ['all_crime'],
        frequency: 'weekly',
        channels: ['in_app']
      });
      setView('form');
      setEditingId(null);
    } else {
      setView(defaultTab || 'notifications');
    }
  }, [isOpen, pendingAlertData, defaultTab]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      if (e.key !== 'Escape') return;
      if (isCrimeDropdownOpen) {
        setIsCrimeDropdownOpen(false);
        return;
      }
      onClose();
      setTimeout(() => document.activeElement?.blur(), 0);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen, onClose, isCrimeDropdownOpen]);

  useEffect(() => {
    if (!isCrimeDropdownOpen) return;
    const handle = (e) => {
      const insideTrigger = crimeButtonRef.current?.contains(e.target);
      const insideMenu = crimeMenuRef.current?.contains(e.target);
      if (!insideTrigger && !insideMenu) setIsCrimeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isCrimeDropdownOpen]);


  const isCrimeDisabled = useCallback((value, current) => {
    if (value === 'all_crime') return false;
    if (current.includes('all_crime')) return true;
    const parent = crimeTypesList.find(t => t.subTypes?.some(s => s.value === value));
    return !!(parent && current.includes(parent.value));
  }, [crimeTypesList]);

  const toggleCrime = useCallback((value) => {
    setFormData(prev => {
      const current = prev.crimeTypes || [];
      if (value === 'all_crime') {
        return { ...prev, crimeTypes: current.includes('all_crime') ? [] : ['all_crime'] };
      }
      if (current.includes(value)) {
        return { ...prev, crimeTypes: current.filter(v => v !== value) };
      }
      const category = crimeTypesList.find(t => t.value === value && t.subTypes);
      const subValues = category?.subTypes?.map(s => s.value) || [];
      return {
        ...prev,
        crimeTypes: [
          ...current.filter(v => v !== 'all_crime' && !subValues.includes(v)),
          value
        ]
      };
    });
  }, [crimeTypesList]);

  const getCrimeMenuMaxHeight = () => {
    if (!crimeButtonRef.current) return 300;
    const bottom = crimeButtonRef.current.getBoundingClientRect().bottom;
    const available = window.innerHeight - bottom - 24;
    return Math.min(Math.max(available, 120), Math.floor(window.innerHeight * 0.6));
  };

  const handleEdit = useCallback((alert) => {
    setFormData({
      locationName: alert.locationName,
      locationType: alert.locationType,
      crimeTypes: getAlertCrimeTypes(alert),
      frequency: alert.frequency,
      channels: [...alert.channels]
    });
    setEditingId(alert.id);
    setView('form');
  }, []);

  const handleSave = useCallback(async () => {
    const types = formData.crimeTypes || [];
    if (!types.length || !formData.locationName || !formData.channels.length) return;
    setSaving(true);
    try {
      if (editingId) {
        await onUpdate(editingId, {
          crimeTypes: types,
          frequency: formData.frequency,
          channels: formData.channels
        });
      } else {
        await onSave({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          locationName: formData.locationName,
          locationType: formData.locationType,
          crimeTypes: types,
          frequency: formData.frequency,
          channels: formData.channels,
          active: true,
          createdAt: new Date().toISOString()
        });
      }
      setView('manage');
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }, [formData, editingId, onSave, onUpdate]);

  const handleCancel = useCallback(() => {
    setView('manage');
    setEditingId(null);
  }, []);

  const toggleChannel = useCallback((ch) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(ch)
        ? prev.channels.filter(c => c !== ch)
        : [...prev.channels, ch]
    }));
  }, []);

  const toggleSelectNotif = (id) => {
    setSelectedNotifIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };
  const isAllSelected =
    sortedNotifications.length > 0 &&
    selectedNotifIds.length === sortedNotifications.length;
  const toggleSelectAll = () => {
    setSelectedNotifIds(isAllSelected ? [] : sortedNotifications.map(n => n.id));
  };
  const handleMarkSelectedRead = async () => {
    const unreadSelected = selectedNotifIds.filter(
      id => notifications.find(n => n.id === id && !n.read)
    );
    if (unreadSelected.length > 0) await onMarkRead(unreadSelected);
    setSelectedNotifIds([]);
  };

  const canSave = (formData.crimeTypes || []).length > 0 &&
    formData.channels.length > 0 &&
    !!formData.locationName &&
    !saving;

  const displayAlerts = [...alerts].reverse();

  if (!isOpen) return null;

  return createPortal(
    <div className="al-overlay" onClick={onClose}>
      <div className="al-modal" onClick={(e) => e.stopPropagation()}>

        <div className="al-header">
          <div className="al-header-top">
            <div className="al-title">
              <Bell size={18} />
              <h2>{view === 'form' ? (editingId ? 'Edit Alert' : 'New Alert') : 'Alerts'}</h2>
            </div>
            <button className="al-close-btn" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          {view !== 'form' && (
            <div className="al-tabs">
              <button
                className={`al-tab${view === 'notifications' ? ' al-tab--active' : ''}`}
                onClick={() => setView('notifications')}
              >
                Notifications
                {unreadCount > 0 && (
                  <span className="al-tab-badge">{unreadCount}</span>
                )}
              </button>
              <button
                className={`al-tab${view === 'manage' ? ' al-tab--active' : ''}`}
                onClick={() => setView('manage')}
              >
                Manage Alerts
                {alerts.length > 0 && (
                  <span className="al-tab-count">{alerts.length}</span>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="al-body">

          {view === 'notifications' && (
            <>
              {sortedNotifications.length > 0 ? (
                <>
                  <div className="al-notifications-toolbar">
                    <label className="al-select-all">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                      />
                      <span>Select All</span>
                    </label>
                    <div className="al-toolbar-actions">
                      {selectedNotifIds.length > 0 && (
                        <button className="al-toolbar-btn" onClick={handleMarkSelectedRead}>
                          Mark as Read
                        </button>
                      )}
                      {unreadCount > 0 && (
                        <button className="al-toolbar-btn" onClick={onMarkAllRead}>
                          Mark All Read
                        </button>
                      )}
                    </div>
                  </div>
                  <ul className="al-notif-list">
                    {sortedNotifications.map((notif) => (
                      <li
                        key={notif.id}
                        className={`al-notif-item${!notif.read ? ' al-notif-item--unread' : ''}`}
                      >
                        <label className="al-notif-check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedNotifIds.includes(notif.id)}
                            onChange={() => toggleSelectNotif(notif.id)}
                          />
                        </label>
                        <div className="al-notif-body">
                          <div className="al-notif-title">{notif.title}</div>
                          {notif.summary && (
                            <div className="al-notif-summary">{notif.summary}</div>
                          )}
                          <div className="al-notif-meta">
                            <MapPin size={11} />
                            <span>{notif.locationName}</span>
                            <span className="al-notif-sep">·</span>
                            <span>{formatDate(notif.receivedAt)}</span>
                          </div>
                        </div>
                        {!notif.read && <span className="al-unread-dot" aria-label="Unread" />}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="al-empty">
                  <div className="al-empty-icon"><Bell size={36} /></div>
                  <h3>No alerts available</h3>
                  <p>Your alerts will appear here when new crime data is available for your saved locations.</p>
                </div>
              )}
            </>
          )}

          {view === 'manage' && (
            <>
              {alerts.length === 0 ? (
                <div className="al-empty">
                  <div className="al-empty-icon"><Bell size={36} /></div>
                  <h3>No alerts configured</h3>
                  <p>Select a location and click <strong>Set Alert</strong> to start receiving crime updates.</p>
                </div>
              ) : (
                <ul className="al-list">
                  {displayAlerts.map((alert) => (
                    <li key={alert.id} className="al-item">
                      <div className="al-item-main">
                        <div className="al-item-location">
                          <MapPin size={14} className="al-item-pin" />
                          <span className="al-item-location-name">{alert.locationName}</span>
                          <span className="al-item-type-badge">{alert.locationType}</span>
                        </div>
                        <div className="al-item-tags">
                          {getAlertCrimeTypes(alert).map(ct => (
                            <span key={ct} className="al-tag al-tag--crime">
                              {getCrimeLabel(ct)}
                            </span>
                          ))}
                          <span className="al-tag al-tag--frequency">
                            {alert.frequency.charAt(0).toUpperCase() + alert.frequency.slice(1)}
                          </span>
                          {alert.channels.map(ch => (
                            <span key={ch} className="al-tag al-tag--channel">
                              {ch === 'in_app' ? 'In-App' : 'Email'}
                            </span>
                          ))}
                        </div>
                        {alert.createdAt && (
                          <span className="al-item-date">Added {formatDate(alert.createdAt)}</span>
                        )}
                      </div>
                      <div className="al-item-actions">
                        <button className="al-edit-btn" onClick={() => handleEdit(alert)} aria-label="Edit alert">
                          <Pencil size={14} />
                        </button>
                        <button className="al-delete-btn" onClick={() => onDelete(alert.id)} aria-label="Remove alert">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {view === 'form' && (
            <div className="al-form">
              <button className="al-back-btn" onClick={handleCancel}>
                <ChevronLeft size={16} />
                Back to Alerts
              </button>

              <div className="al-form-section">
                <span className="al-form-label">Location</span>
                <div className="al-form-location">
                  <MapPin size={14} />
                  <span className="al-form-location-name">
                    {formData.locationName || 'No location selected'}
                  </span>
                  {formData.locationType && (
                    <span className="al-item-type-badge">{formData.locationType}</span>
                  )}
                </div>
                {!formData.locationName && (
                  <p className="al-form-hint">Select a location from the map first, then click Set Alert.</p>
                )}
              </div>

              <div className="al-form-section">
                <span className="al-form-label">Crime Type</span>
                <div className="al-custom-dropdown">
                  <div
                    role="button"
                    tabIndex={0}
                    ref={crimeButtonRef}
                    className="al-dropdown-trigger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCrimeDropdownOpen(o => !o);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsCrimeDropdownOpen(o => !o);
                      }
                    }}
                  >
                    {(formData.crimeTypes || []).length === 0 ? (
                      <span className="al-trigger-placeholder">Select Crime Type</span>
                    ) : (
                      (formData.crimeTypes || []).map(ct => (
                        <span key={ct} className="al-trigger-chip">
                          {getCrimeLabel(ct)}
                          <button
                            type="button"
                            className="al-trigger-chip-x"
                            tabIndex={-1}
                            aria-label={`Remove ${getCrimeLabel(ct)}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCrime(ct);
                            }}
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))
                    )}
                  </div>

                  {isCrimeDropdownOpen && crimeButtonRef.current && createPortal(
                    <div
                      ref={crimeMenuRef}
                      className="al-dropdown-menu"
                      style={{
                        position: 'absolute',
                        top: `${crimeButtonRef.current.getBoundingClientRect().bottom + window.scrollY + 4}px`,
                        left: `${crimeButtonRef.current.getBoundingClientRect().left + window.scrollX}px`,
                        minWidth: `${crimeButtonRef.current.getBoundingClientRect().width}px`,
                        maxHeight: `${getCrimeMenuMaxHeight()}px`,
                        zIndex: 10002
                      }}
                    >
                      <div className="al-dropdown-section">

                        <label className={`al-dropdown-item${(formData.crimeTypes || []).includes('all_crime') ? ' al-dropdown-item--checked' : ''}`}>
                          <input
                            type="checkbox"
                            checked={(formData.crimeTypes || []).includes('all_crime')}
                            onChange={() => toggleCrime('all_crime')}
                          />
                          All Crime
                        </label>

                        {crimeTypesList.filter(t => t.value !== 'all_crime').map((type) => {
                          const disabled = isCrimeDisabled(type.value, formData.crimeTypes || []);
                          return (
                            <div key={type.value}>
                              <div className="al-dropdown-submenu">
                                <label
                                  className={`al-dropdown-item al-dropdown-item--category-border${disabled ? ' al-dropdown-item--disabled' : ''}${(formData.crimeTypes || []).includes(type.value) ? ' al-dropdown-item--checked' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(formData.crimeTypes || []).includes(type.value)}
                                    disabled={disabled}
                                    onChange={() => !disabled && toggleCrime(type.value)}
                                  />
                                  {type.label}
                                </label>
                                {type.subTypes && (
                                  <div className="al-dropdown-submenu">
                                    {type.subTypes.map(sub => {
                                      const subDisabled = isCrimeDisabled(sub.value, formData.crimeTypes || []);
                                      return (
                                        <label
                                          key={sub.value}
                                          className={`al-dropdown-item${subDisabled ? ' al-dropdown-item--disabled' : ''}${(formData.crimeTypes || []).includes(sub.value) ? ' al-dropdown-item--checked' : ''}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={(formData.crimeTypes || []).includes(sub.value)}
                                            disabled={subDisabled}
                                            onChange={() => !subDisabled && toggleCrime(sub.value)}
                                          />
                                          {sub.label}
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                      </div>
                    </div>,
                    document.body
                  )}
                </div>
                <p className={`al-form-error${(formData.crimeTypes || []).length === 0 && !isCrimeDropdownOpen ? '' : ' al-form-error--hidden'}`}>
                  Select at least one type of crime
                </p>
              </div>

              <div className="al-form-section">
                <span className="al-form-label">Frequency</span>
                <div className="al-pill-group">
                  {FREQUENCY_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`al-pill${formData.frequency === value ? ' al-pill--active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, frequency: value }))}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="al-form-section">
                <span className="al-form-label">Alert Type</span>
                <div className="al-pill-group">
                  {CHANNEL_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`al-pill${formData.channels.includes(value) ? ' al-pill--active' : ''}`}
                      onClick={() => toggleChannel(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className={`al-form-error${formData.channels.length === 0 ? '' : ' al-form-error--hidden'}`}>
                  Select at least one alert type.
                </p>
              </div>

              <div className="al-form-actions">
                <button className="al-cancel-btn" onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className="al-save-btn"
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Set Alert'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>,
    document.body
  );
};

export default AlertsModal;
